"use client";

import React from "react";
import Image from "next/image";

interface TextPart {
  text: string;
  isGradient?: boolean;
  startOnNewLine?: boolean;
}

interface PageHeroSectionProps {
  /** Small subtitle text above the main headline */
  subtitle: string;
  /** Array of text parts for the headline - allows mixing normal and gradient text */
  headlineParts: TextPart[];
  /** Whether to show the vertical line below the headline */
  showVerticalLine?: boolean;
  /** Optional background image path - when provided, adds full-screen background with overlay */
  backgroundImage?: string;
  /** Optional description paragraph below the headline */
  description?: string;
}

/**
 * A reusable hero section component with gradient text support
 * Used for Services, Regulatory Resources, Personas, and similar pages
 */
export default function PageHeroSection({
  subtitle,
  headlineParts,
  showVerticalLine = false,
  backgroundImage,
  description,
}: PageHeroSectionProps) {
  return (
    <section
      className={`relative flex w-full flex-col items-center justify-center px-5 text-center md:px-12 lg:px-[100px] ${
        backgroundImage
          ? "min-h-[80vh] overflow-hidden md:min-h-screen"
          : "pt-16 lg:py-50"
      }`}
    >
      {/* Background Image (optional) */}
      {backgroundImage && (
        <>
          <Image
            src={backgroundImage}
            alt={subtitle}
            fill
            priority
            className="object-cover"
          />
          {/* Dark Overlay for text visibility */}
          <div className="absolute inset-0 bg-black/50" />
        </>
      )}

      {/* Content Container */}
      <div className={backgroundImage ? "relative z-10" : ""}>
        <p className="font-raleway mb-5 text-xl text-white md:mb-10">
          {subtitle}
        </p>
        <h1 className="font-Raleway max-w-6xl text-[2rem] leading-tight font-extrabold tracking-wide text-white uppercase md:text-5xl">
          {headlineParts.map((part, index) => (
            <React.Fragment key={index}>
              {part.startOnNewLine && <br className="hidden md:block" />}
              {part.isGradient ? (
                <span className="from-brand-red to-brand-dark-red bg-linear-to-r bg-clip-text text-transparent">
                  {part.text}
                </span>
              ) : (
                part.text
              )}
            </React.Fragment>
          ))}
        </h1>

        {/* Description (optional) */}
        {description && (
          <p className="font-raleway mx-auto mt-5 max-w-5xl text-sm leading-[1.3] text-white/90 md:text-base">
            {description}
          </p>
        )}

        {/* Vertical Line */}
        {showVerticalLine && (
          <div className="mx-auto mt-16 h-24 w-px bg-white" />
        )}
      </div>
    </section>
  );
}
